import { DetailedTableInfoDc } from "@evergis/api";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useNotification } from "../notifications/useNotification";
import { useTablePreviewUpdate } from "../preview/usePreviewUpdate";
import { useLoading } from "../useLoading";
import { useTrigger } from "../useTrigger";
import { getColumnsUpdate } from "./getColumnsUpdate";
import { updateLayer, updateTileLayer } from "../../resources/layers";
import { addColumnsToLayerConfiguration } from "../../evergis/core/layer/addColumnsToLayerConfiguration";
import { removeColumnsFromLayerConfiguration } from "../../evergis/core/layer/removeColumnsFromLayerConfiguration";
import { invalidateTable } from "../../resources/invalidations";
import { usePutTable } from "./usePutTable";
import { useDefaultScaleRange } from "../style/useDefaultScaleRange";
import { TableAttribute } from "../../components/Dialogs/EditResource/components/Table/TableAttributeItem";
import { ResourceMeta } from "../useResourceMeta";
import { LayerInfo, TableInfo } from "../../interfaces/resourceInfo";

// eslint-disable-next-line max-lines-per-function
export const useTableInfoUpdate = (
  tableInfo: TableInfo,
  layerRefs: LayerInfo[],
  meta: ResourceMeta,
  attributes: TableAttribute[],
) => {
  const { t } = useTranslation("dialogs");
  const defaultScaleRange = useDefaultScaleRange();
  const { name: tableName, columns } = tableInfo;
  const columnsUpdate = useMemo(() => getColumnsUpdate(attributes, columns), [attributes, columns]);
  const [putTable, { tableUpdate, tableUpdating, tableUpdateError }] = usePutTable();
  const [updateLayerMutation] = updateLayer.use();
  const [updateTileLayerMutation] = updateTileLayer.use();
  const { updatePreview, previewUpdate, needUpdatePreview } = useTablePreviewUpdate(tableName, meta.preview);
  const previewUpdated = !!(needUpdatePreview ? previewUpdate : tableUpdate);
  const updating = useLoading(tableUpdating, previewUpdated);

  const update = useCallback(() => {
    const newColumns = attributes.map((item, index) => ({ ...columns[index], ...item }));

    putTable({
      ...(tableInfo as DetailedTableInfoDc),
      columns: newColumns,
      configuration: {
        ...(tableInfo as DetailedTableInfoDc).configuration,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        attributesConfiguration: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ...(tableInfo as DetailedTableInfoDc).configuration?.attributesConfiguration,
          attributes: newColumns,
        },
      },
      alias: meta.alias,
      description: meta.description,
    });
  }, [tableInfo, putTable, attributes, meta, columns]);

  useEffect(() => {
    if (tableUpdate && columnsUpdate.columnsDelete.length) {
      layerRefs.forEach(async layerRef => {
        if (layerRef.type === "PostgresLayerService") {
          await updateLayerMutation({
            input: {
              configuration: removeColumnsFromLayerConfiguration(layerRef, columnsUpdate.columnsDelete),
              defaultScaleRange,
            },
          });
        }

        if (layerRef.type === "PostgresTileLayerService") {
          await updateTileLayerMutation({
            input: {
              configuration: removeColumnsFromLayerConfiguration(layerRef, columnsUpdate.columnsDelete),
              defaultScaleRange,
            },
          });
        }
      });
    }
  }, [tableUpdate, columnsUpdate, layerRefs, updateLayerMutation, defaultScaleRange, updateTileLayerMutation]);

  const resetTable = useCallback(() => {
    if (columnsUpdate.columnsAdd.length) {
      layerRefs.forEach(async layerRef => {
        if (layerRef.type === "PostgresLayerService") {
          await updateLayerMutation({
            input: {
              configuration: addColumnsToLayerConfiguration(layerRef, columnsUpdate.columnsAdd),
              defaultScaleRange,
            },
          });
        }

        if (layerRef.type === "PostgresTileLayerService") {
          await updateTileLayerMutation({
            input: {
              configuration: addColumnsToLayerConfiguration(layerRef, columnsUpdate.columnsAdd),
              defaultScaleRange,
            },
          });
        }
      });
    }

    invalidateTable(
      tableInfo.name,
      layerRefs.map(({ name }) => name),
    );
  }, [
    columnsUpdate.columnsAdd,
    tableInfo.name,
    layerRefs,
    updateLayerMutation,
    defaultScaleRange,
    updateTileLayerMutation,
  ]);

  useNotification(!updating && tableUpdate, {
    success: true,
    title: t("editResource.changesApplied"),
    description: meta.alias,
  });

  useNotification(tableUpdateError, {
    error: true,
    title: t("editResource.errorSavingTable"),
    description: `${meta.alias}. ${t("editResource.tryAgainOrCheckData")}`,
  });

  useTrigger(tableUpdate && !tableUpdating, updatePreview);
  useTrigger(tableUpdate && !updating, resetTable);

  return useMemo(() => ({ update, tableUpdate, updating }), [update, tableUpdate, updating]);
};
